import { RequestService } from 'Utils/Api';
import { FREE_TRIAL_PROMO } from 'Utils/Constants';
import { timestampToDate } from 'Utils/Format';
import { trackPaymentError } from 'Utils/PaymentMethods';
import { getChangeSubscriptionType, getPromoCode, getSubscriptionParameters } from 'Api/EvergentApi';
import { logger } from 'Utils/Logger';
import { getDefaultConfig } from 'Utils/Store';

const APPLE_PAY_VERSION_NUMBER = 6;

export const isApplePayAvailable = () => {
  const deviceCheck = { deviceEnabled: false, isAvailable: false };
  try {
    const device = window.ApplePaySession;
    if (device) {
      deviceCheck.deviceEnabled = true;
    }
  } catch (err) {
    return deviceCheck;
  }

  try {
    const available = window.ApplePaySession.canMakePayments();
    if (available) {
      deviceCheck.isAvailable = true;
    }
  } catch (err) {
    return deviceCheck;
  }
  return deviceCheck;
};

const applePayApi = (url, payload) => {
  const { foxApi, accountApiKey } = getDefaultConfig();
  return RequestService.send({
    url: `${foxApi}/applepay${url}`,
    method: 'POST',
    data: payload,
    authenticate: true,
    customHeaders: { 'x-api-key': accountApiKey },
  }).then((res) => res && res.data);
};

const performValidation = (url, domain) => {
  const { applePayMerchantId } = getDefaultConfig();
  const data = {
    url,
    body: {
      merchantIdentifier: applePayMerchantId,
      displayName: 'Fox',
      initiative: 'web',
      initiativeContext: domain,
    },
  };
  return applePayApi('/paymentSession', data);
};

export const applePayment = (appleData, payload) => {
  let changeType;
  const { isSkipDefaultPromo, plans, promoCode, purchaseMode, subscription } = payload;
  const { selectedPlanId, selectedPlan } = plans;
  const { promotionID } = selectedPlan;
  const { isChangePlan } = purchaseMode;
  const couponCode = getPromoCode(promoCode);
  // {} object to avoid adding redundant "Operation Type" param.
  // BE also adds this param before proxying request to Evergent
  const parameters = getSubscriptionParameters({ autoCouponCode: !!promoCode.emailCoupon });

  const endpoint = isChangePlan ? '/changeService' : '/subscription';
  // if change plan, determine upgrade or downgrade change type operation.
  if (isChangePlan) {
    changeType = getChangeSubscriptionType({ selectedPlan, subscription });
  }

  const service = {
    couponCode,
    promotionId: promotionID,
    serviceType: 'PACKAGE',
    serviceId: selectedPlanId,
    quantity: '1',
    changeType,
  };

  const data = {
    ...appleData,
    service,
    isSkipPromoCode: isSkipDefaultPromo,
    parameters,
  };
  return applePayApi(endpoint, data);
};

const getPaymentRequest = (payload) => {
  const { checkout, isSkipDefaultPromo, plans, promoCode, subscription } = payload;
  const {
    selectedPlan: { freePromo, hasMerchandise, hasFreePromo },
    selectedPlanPrice,
  } = plans;
  const { totalTax } = checkout;
  const { isNewUser, isChurnedUser, isTrialistUser, validityTill } = subscription;

  let totalPrice = (selectedPlanPrice + totalTax).toFixed(2) || 0;

  // Determining Label to show
  let defaultTotalLabel = `Due today (plus applicable taxes)`;
  if (isTrialistUser && validityTill) {
    defaultTotalLabel = `Due on ${timestampToDate(validityTill, 2)} (plus applicable taxes)`;
  }
  if (hasFreePromo && (isNewUser || isChurnedUser)) {
    const { nextBillingDateTime } = freePromo;
    const billDate = timestampToDate(nextBillingDateTime, 2);
    defaultTotalLabel = `Due on ${billDate} (plus applicable taxes)`;
  }

  if (promoCode.isValid) {
    defaultTotalLabel = `${promoCode.totalLabel} (plus applicable taxes)`;
  }

  // Additional subtotals dislayed above total if promo's are available
  const items = [
    {
      label: defaultTotalLabel,
      type: 'final',
      amount: selectedPlanPrice,
    },
  ];

  if (promoCode.isValid) {
    const priceWithPromoCode = promoCode.priceToBeCharged > 0 ? promoCode.priceToBeCharged : 0;
    const difference = (priceWithPromoCode - selectedPlanPrice).toFixed(2) || 0;
    totalPrice = (priceWithPromoCode + totalTax).toFixed(2) || 0;

    items.push({
      label: promoCode.promoDescription || 'Promotion',
      type: 'final',
      amount: difference,
    });
  } else if (!isSkipDefaultPromo && hasFreePromo && (isNewUser || isChurnedUser)) {
    const { amount, displayName, promotionID } = freePromo;
    const priceToCharge = amount || 0;
    const label = FREE_TRIAL_PROMO === promotionID ? displayName : 'Promotion';
    totalPrice = (priceToCharge + totalTax).toFixed(2) || 0;

    items.push({
      label,
      type: 'final',
      amount: priceToCharge,
    });
  }

  const requiredShippingFields = hasMerchandise ? ['email', 'postalAddress'] : ['email'];

  return {
    currencyCode: 'USD',
    countryCode: 'US',
    lineItems: items,
    total: {
      label: 'Fox Today (plus applicable taxes)',
      amount: totalPrice,
    },
    requiredShippingContactFields: requiredShippingFields,
    requiredBillingContactFields: ['postalAddress'],
    supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
    merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
  };
};

const getOnValidateMerchant = (session, payload) => {
  return (event) => {
    logger.started('applepay merchant validatation', payload);
    performValidation(event.validationURL, window.location.hostname)
      .then((response) => {
        logger.completed('applepay merchant validatation', response);
        session.completeMerchantValidation(response);
      })
      .catch((err) => {
        logger.completed('applepay merchant validatation', payload, err);
        console.log('Validation error ', JSON.stringify(err));
        const { user } = payload;
        const tracking = {
          trackPaymentMethod: 'apple pay',
          errorMessage: err.message,
          user,
        };
        trackPaymentError(tracking);
        session.abort();
      });
  };
};

const getOnPaymentAuth = (resolve, reject, session, payload) => {
  return (event) => {
    logger.apiRequest('applepay subscription', { event: event.payment, payload });
    applePayment(event.payment, payload)
      .then((response) => {
        logger.apiResponse('applepay subscription', response);
        if (!response || !response.orderId) {
          throw Error('payment service failure: orderId missing on payment success');
        }
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        resolve({ isSuccess: true, orderID: response.orderId });
      })
      .catch((err) => {
        logger.apiError('applepay subscription', payload, err);
        console.log('Payment error ', err);
        const { user } = payload;
        const tracking = {
          trackPaymentMethod: 'apple pay',
          errorMessage: err.message,
          user,
        };
        trackPaymentError(tracking);
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        reject({ isSuccess: false, errorCode: '', errorMessage: err.message });
      });
  };
};

const getOnCancel = (resolve) => {
  return () => {
    console.log('ApplePay session canceled');
    resolve();
  };
};

export const applepaySubscription = (payload) => {
  return new Promise((resolve, reject) => {
    const paymentRequest = getPaymentRequest(payload);
    const session = new window.ApplePaySession(APPLE_PAY_VERSION_NUMBER, paymentRequest);
    session.onvalidatemerchant = getOnValidateMerchant(session, payload);
    session.onpaymentauthorized = getOnPaymentAuth(resolve, reject, session, payload);
    session.oncancel = getOnCancel(resolve);
    session.begin();
  });
};
